package com.essntl.features.request.ui.addeditrequest.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.ui.addeditrequest.AddEditRequestState
import com.essntl.features.request.ui.addeditrequest.AddEditRequestValidation
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.date.EsDateRangeInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun RequestBaseInformation(
    state: AddEditRequestState,
    onRequestChange: (RequestModel) -> Unit,
    enabled: Boolean,
    validation: AddEditRequestValidation,
) {
    val request = state.request.value
    val isLoading = state.addEditRequest.isLoading

    var isExpanded by rememberSaveable { mutableStateOf(true) }

    val selectedAreaList =
        rememberContains(
            state.getAllArea.data.orEmpty(),
            request.areas.orEmpty().toSet(),
        ) { area -> area.id }

    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(
                    color = MaterialTheme.esColorScheme.bgSecondary,
                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                )
                .padding(
                    vertical = 3.spacing,
                    horizontal = 2.5f.spacing,
                ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier =
                Modifier
                    .fillMaxWidth(),
        ) {
            Text(
                text = "Base Information",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )

            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier =
                    Modifier
                        .graphicsLayer {
                            transformOrigin = TransformOrigin.Center
                            rotationZ = if (isExpanded) 180f else 0f
                        }
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                        ) {
                            isExpanded = !isExpanded
                        },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = request.title ?: "",
                    onValueChange = {
                        validation.addEditRequestForm.title.validate(it)
                        onRequestChange(
                            request.copy(
                                title = it,
                            ),
                        )
                    },
                    state = validation.addEditRequestForm.title.state,
                    placeholder = "Enter Title",
                    message = "This field is required",
                    label = "Title",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInputDropdownMenu(
                    items = state.getAllArea.data.orEmpty(),
                    selectedItems = selectedAreaList,
                    text = { it.name ?: "" },
                    onItemsChanged = { areas ->
                        validation.addEditRequestForm.areas.validate(areas.map { it.id })
                        onRequestChange(
                            request.copy(
                                areas = areas.map { it.id },
                            ),
                        )
                    },
                    label = "Area",
                    placeholder = "Select Area",
                    message = "This field is required",
                    state = validation.addEditRequestForm.areas.state,
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                Row {
                    EsDateRangeInput(
                        value = "",
                        onValueChange = {
                            onRequestChange(
                                request.copy(
                                    startDate = "",
                                ),
                            )
                        },
                        fromDate = "",
                        toDate = "",
                        onDateRangeChange = { fromDate, toDate ->
                            validation.addEditRequestForm.dateRange.validate(fromDate, toDate)
                            onRequestChange(
                                request.copy(
                                    startDate = "",
                                    endDate = "",
                                ),
                            )
                        },
                        label = "From",
                        message = "This field is required.",
                        state = validation.addEditRequestForm.dateRange.firstState,
                        enabled = enabled && !isLoading,
                        modifier =
                            Modifier
                                .weight(1f),
                    )

                    Spacer(modifier = Modifier.width(1.5f.spacing))

                    EsDateRangeInput(
                        value = "",
                        onValueChange = {
                            validation.addEditRequestForm.dateRange.validate(
                                "",
                                it,
                            )
                            onRequestChange(
                                request.copy(
                                    endDate = "",
                                ),
                            )
                        },
                        fromDate = "",
                        toDate = "",
                        onDateRangeChange = { fromDate, toDate ->
                            validation.addEditRequestForm.dateRange.validate(fromDate, toDate)
                            onRequestChange(
                                request.copy(
                                    startDate = "",
                                    endDate = "",
                                ),
                            )
                        },
                        label = "To",
                        message = "This field is required",
                        state = validation.addEditRequestForm.dateRange.secondState,
                        enabled = enabled && !isLoading,
                        modifier =
                            Modifier
                                .weight(1f),
                    )
                }

                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = request.extras ?: "",
                    onValueChange = {
                        onRequestChange(
                            request.copy(
                                extras = it,
                            ),
                        )
                    },
                    label = "Details about the request",
                    enabled = enabled && !isLoading,
                    singleLine = false,
                )
            }
        }
    }
}
